/* eslint-disable camelcase */
import React, {
	useState, useEffect, Fragment, forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import {
	Elements, CardElement, useStripe, useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { PaymentService } from '@services';
import { STRIPE_KEY } from '@config';
import utils from '@utils';
import StateSelection from '../../StateSelection/StateSelection';

import './CreditCardForm.scss';

// Use a custom button with a Ref, so we can trigger onClick from outside
const CustomButton = forwardRef((props, ref) => (
	<button
		ref={ref}
		type="button"
		disabled={props.disabled}
		onClick={props.onClick}
		className="btn btn-primary"
	>
		{ props.loading ? 'Loading' : props.submitLabel }
	</button>
));

CustomButton.propTypes = {
	disabled    : PropTypes.bool,
	onClick     : PropTypes.func,
	loading     : PropTypes.bool,
	submitLabel : PropTypes.string.isRequired,
};

CustomButton.defaultProps = {
	disabled : false,
	onClick  : undefined,
	loading  : false,
};

const Form = ({
	userAddress, onSubmit, successCallback, cancelCallback, cancelLabel, submitLabel, setCustomRef, expandAddress, hideButtons, onError,
}) => {
	const stripe = useStripe();
	const elements = useElements();

	const [form, setForm] = useState({
		_state        : userAddress._state || '',
		name          : userAddress.name || '',
		address_line1 : userAddress.address_line1 || '',
		address_city  : userAddress.address_city || '',
		address_state : userAddress.address_state || '',
		address_zip   : userAddress.address_zip || '',
	});

	const [validations, setValidations] = useState({
		_state        : false, // true = has error
		name          : false,
		address_line1 : false,
		address_city  : false,
		address_zip   : false,
	});

	const [editAddress, setEditAddress] = useState(expandAddress);
	const [loading, setLoading] = useState(false);

	const buttonRef = React.useRef(null);
	const { current } = buttonRef;

	useEffect(() => {
		if (current && setCustomRef) {
			setCustomRef(current);
		}
	}, [current, setCustomRef]);

	const savePaymentMethod = (TokenId) => {
		PaymentService.addPaymentMethod({ PaymentCard : { TokenId } })
			.then(() => {
				utils.notify({
					type    : 'success',
					message : 'Payment Method added successfully',
				});

				setLoading(false);

				if (successCallback) successCallback();
			})
			.catch(({ response }) => {
				utils.notify({
					type    : 'error',
					message : response.data.Message || 'Error loading client information.',
				});

				setLoading(false);
			});
	};

	const onSubmitForm = async (event) => {
		if (event) event.preventDefault();
		if (!stripe || !elements) return;

		// Validate form
		const newValidations = { ...validations };
		newValidations._state = form._state === '';
		newValidations.name = form.name === '';
		newValidations.address_line1 = form.address_line1 === '';
		newValidations.address_city = form.address_city === '';
		newValidations.address_zip = form.address_zip === '';

		// If any validation property is true, the form is not valid
		const validationError = Object.values(newValidations).some((item) => item);
		const addressError = Object.entries(newValidations).filter(([key]) => key !== 'name').some(([, val]) => val);
		setValidations(newValidations);

		if (validationError) {
			if (onError) onError('error');
			if (addressError) setEditAddress(true);
			return;
		}

		setLoading(true);

		// Get a reference to a mounted CardElement. Elements knows how to find your CardElement
		// because there can only ever be one of each type of element.
		const cardElement = elements.getElement(CardElement);

		// Add form values, US country and USD currency to stripe request
		const { error, token } = await stripe.createToken(cardElement, {
			...form,
			address_country : 'US',
			currency        : 'USD',
		});

		if (error) {
			utils.notify({
				type    : 'error',
				message : error.message || 'Error getting token',
			});

			setLoading(false);
		} else if (onSubmit) {
			onSubmit({ type : 'creditCard', token });
		} else {
			savePaymentMethod(token.id);
		}
	};

	const buildSummary = () => {
		const {
			address_line1, address_city, address_state, address_zip,
		} = form;

		if (address_line1 && address_city && address_state && address_zip) {
			return (<p>{`${address_line1}, ${address_city}, ${address_state}, ${address_zip}`}</p>);
		}

		return <p>Incomplete address</p>;
	};

	const onFormChange = (property) => (event) => {
		let value = '';
		if (event && event.target && event.target.value) value = event.target.value;
		setValidations((prev) => ({ ...prev, [property] : false }));
		setForm((prev) => ({
			...prev,
			[property] : value,
		}));
	};

	const onStateChange = (event) => {
		let value = '';
		if (event && event.target && event.target.value && event.target.value !== 'Select State') value = event.target.value;

		// Get label of selected option
		const index = event.nativeEvent.target.selectedIndex;
		const label = event.nativeEvent.target[index].text;

		setValidations((prev) => ({ ...prev, _state : value === '' }));
		setForm((prev) => ({
			...prev,
			address_state : label,
			_state        : value,
		}));
	};

	const toggleAddressFields = () => {
		setEditAddress((prev) => !prev);
	};

	return (
		<Fragment>
			<div className={`row form-group ${validations.name ? 'has-error' : ''}`}>
				<div className="col-xs-12">
					<label htmlFor="name">Card Holder Full Name</label>
				</div>
				<div className="col-xs-12">
					<input
						type="text"
						id="name"
						value={form.name}
						onChange={onFormChange('name')}
						className="form-control"
						placeholder="John Doe"
					/>
				</div>
			</div>
			<div className="row form-group">
				<div className="col-xs-12">
					<label htmlFor="CardElement">Card Number</label>
				</div>
				<div className="col-xs-12">
					<CardElement
						options={{
							style : {
								base : {
									'::placeholder' : {
										color : '#b6cce2',
									},
								},
							},
							hidePostalCode : true,
						}}
					/>
				</div>
			</div>
			<div id="credit-card-separator-dashed" />
			<div id="credit-card-billing-address" className="row">
				<div className="col-xs-6 text-left">
					<strong>Billing Address</strong>
				</div>
				<div className="col-xs-6 text-right">
					<button
						type="button"
						className="btn btn-link"
						onClick={toggleAddressFields}
					>
						<i className={`rkf ${editAddress ? 'rkf-chevron-up' : 'rkf-chevron-down'}`} />
						Edit Address
					</button>
				</div>
			</div>
			<div>
				<Choose>
					<When condition={editAddress}>
						<div className={`row form-group ${validations.address_line1 ? 'has-error' : ''}`}>
							<div className="col-xs-12">
								<label htmlFor="address_line1">Address</label>
							</div>
							<div className="col-xs-12">
								<input
									type="text"
									id="address_line1"
									value={form.address_line1}
									onChange={onFormChange('address_line1')}
									onKeyPress={(e) => { if (e.key === 'Enter') onSubmitForm(e); }}
									className="form-control"
									placeholder="2297 Sundown Lane"
								/>
							</div>
						</div>

						<div className={`row form-group ${validations.address_city ? 'has-error' : ''}`}>
							<div className="col-xs-12">
								<label htmlFor="address_city">City</label>
							</div>
							<div className="col-xs-12">
								<input
									type="text"
									id="address_city"
									value={form.address_city}
									onChange={onFormChange('address_city')}
									onKeyPress={(e) => { if (e.key === 'Enter') onSubmitForm(e); }}
									className="form-control"
									placeholder="Austin"
								/>
							</div>
						</div>

						<div className="row">
							<div className={`col-xs-6 form-group ${validations._state ? 'has-error' : ''}`}>
								<div><label htmlFor="address_state">State</label></div>
								<StateSelection
									id="address_state"
									name="address_state"
									value={form._state}
									onChange={onStateChange}
									className="form-control"
								/>
							</div>

							<div className={`col-xs-6 form-group ${validations.address_zip ? 'has-error' : ''}`}>
								<div><label htmlFor="address_zip">Zip Code</label></div>
								<input
									type="text"
									id="address_zip"
									value={form.address_zip}
									onChange={onFormChange('address_zip')}
									onKeyPress={(e) => { if (e.key === 'Enter') onSubmitForm(e); }}
									className="form-control"
									placeholder="78749"
								/>
							</div>
						</div>
					</When>
					<Otherwise>
						{buildSummary()}
					</Otherwise>
				</Choose>
			</div>

			<div className={`row${hideButtons ? ' hidden' : ''}`}>
				<div className="col-xs-12" id="credit-card-btn-container">
					<button
						type="button"
						className="btn btn-link"
						onClick={cancelCallback}
					>
						{cancelLabel}
					</button>
					<CustomButton
						ref={buttonRef}
						disabled={!stripe || loading}
						onClick={onSubmitForm}
						loading={loading}
						submitLabel={submitLabel}
					/>
				</div>
			</div>
		</Fragment>
	);
};

Form.propTypes = {
	userAddress     : PropTypes.object,
	onSubmit        : PropTypes.func,
	successCallback : PropTypes.func,
	cancelCallback  : PropTypes.func,
	cancelLabel     : PropTypes.string,
	submitLabel     : PropTypes.string,
	expandAddress   : PropTypes.bool,
	setCustomRef    : PropTypes.func,
	hideButtons     : PropTypes.bool,
	onError         : PropTypes.func,
};

Form.defaultProps = {
	userAddress     : {},
	onSubmit        : undefined,
	successCallback : undefined,
	cancelCallback  : undefined,
	cancelLabel     : 'Cancel',
	submitLabel     : 'Save',
	expandAddress   : false,
	setCustomRef    : undefined,
	hideButtons     : false,
	onError         : undefined,
};

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(STRIPE_KEY);

/* eslint-disable react/jsx-props-no-spreading */
const CreditCardForm = (props) => (
	<div id="payment-form">
		<Elements stripe={stripePromise}>
			<Form {...props} />
		</Elements>
	</div>
);

export default CreditCardForm;
